/**
 * Time Utility Functions for Discussion Forum
 *
 * Provides formatting functions for displaying relative times
 * and timestamps in a user-friendly format.
 */

/**
 * Format a timestamp as relative time (e.g., "2 minutes ago")
 *
 * @param {string|Date|null} timestamp - ISO string, Date object, or null
 * @returns {string} Formatted relative time string
 */
export const formatRelativeTime = (timestamp) => {
  if (!timestamp) {
    return 'Sending...';
  }

  try {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 10) {
      return 'Just now';
    } else if (diffSec < 60) {
      return `${diffSec} seconds ago`;
    } else if (diffMin === 1) {
      return '1 minute ago';
    } else if (diffMin < 60) {
      return `${diffMin} minutes ago`;
    } else if (diffHour === 1) {
      return '1 hour ago';
    } else if (diffHour < 24) {
      return `${diffHour} hours ago`;
    } else if (diffDay === 1) {
      return 'Yesterday';
    } else if (diffDay < 7) {
      return `${diffDay} days ago`;
    } else {
      // For older messages, show the date
      return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  } catch (error) {
    console.error('[timeUtils] Error formatting timestamp:', error);
    return 'Unknown time';
  }
};

/**
 * Format a timestamp as a full date/time string
 *
 * @param {string|Date|null} timestamp - ISO string, Date object, or null
 * @returns {string} Formatted date/time string
 */
export const formatFullDateTime = (timestamp) => {
  if (!timestamp) {
    return 'Pending';
  }

  try {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('[timeUtils] Error formatting full timestamp:', error);
    return 'Invalid date';
  }
};
